// Curated field-duty consent copy. Admins pick a template; the hosted RPC still stores the
// published body text. Custom wording is available but tucked behind an explicit opt-in.

export interface ConsentTemplate {
  id: string;
  consentVersion: string;
  title: string;
  description: string;
  bodyText: string;
}

export const CONSENT_TEMPLATES: readonly ConsentTemplate[] = [
  {
    id: "standard-on-duty",
    consentVersion: "field-duty-standard-v1",
    title: "On-Duty Safety Notice",
    description: "Standard foreground location while clocked in for officer safety and dispatch.",
    bodyText: [
      "While you are clocked in on duty, this account may collect your device location at regular intervals.",
      "Location is used for officer safety, missed-checkpoint awareness, and supervisor accountability.",
      "Tracking stops when you clock out or end your duty session.",
      "By accepting, you confirm you understand this on-duty safety practice for this account.",
    ].join("\n\n"),
  },
  {
    id: "healthcare-campus",
    consentVersion: "field-duty-healthcare-v1",
    title: "Healthcare Campus On-Duty Notice",
    description: "Healthcare-oriented language for medical and clinical patrol sites.",
    bodyText: [
      "While clocked in at a healthcare campus, this account may collect foreground location for on-duty safety.",
      "Location supports officer welfare checks, route accountability, and rapid supervisor response.",
      "Tracking is limited to active duty periods and does not continue after clock-out.",
      "By accepting, you agree to this on-duty safety practice for protected healthcare environments.",
    ].join("\n\n"),
  },
  {
    id: "commercial-patrol",
    consentVersion: "field-duty-commercial-v1",
    title: "Commercial Property On-Duty Notice",
    description: "Retail, office, and mixed-use patrol accounts.",
    bodyText: [
      "While on duty at this account, your device may share location during clocked-in periods.",
      "Location helps verify patrol coverage, support officer safety, and document field presence.",
      "Tracking ends when your shift or duty session ends.",
      "By accepting, you acknowledge this on-duty location practice for commercial patrol work.",
    ].join("\n\n"),
  },
] as const;

export const CUSTOM_CONSENT_TEMPLATE_ID = "custom";

export function findConsentTemplate(id: string): ConsentTemplate | null {
  return CONSENT_TEMPLATES.find((template) => template.id === id) ?? null;
}

export function suggestNextConsentVersion(existingVersions: readonly string[]): string {
  const year = new Date().getFullYear();
  const prefix = `field-duty-${year}-`;
  let max = 0;
  for (const version of existingVersions) {
    const match = version.match(new RegExp(`^${prefix}(\\d+)$`));
    if (match !== null) {
      max = Math.max(max, Number.parseInt(match[1] ?? "0", 10));
    }
  }
  return `${prefix}${max + 1}`;
}
